import { Injectable, effect, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';

export type Density = 'comfortable' | 'compact';

export interface layoutConfig {
    preset?: string;
    primary?: string;
    surface?: string | undefined | null;
    // legacy: darkTheme kept for backward compatibility (boolean preference)
    darkTheme?: boolean;
    // new: explicit theme mode (light | dark | system)
    themeMode?: 'light' | 'dark' | 'system';
    menuMode?: string;
    // spacing density for data-dense views; applied as data-density on <html>
    density?: Density;
}

interface LayoutState {
    staticMenuDesktopInactive?: boolean;
    overlayMenuActive?: boolean;
    configSidebarVisible?: boolean;
    staticMenuMobileActive?: boolean;
    menuHoverActive?: boolean;
}

interface MenuChangeEvent {
    key: string;
    routeEvent?: boolean;
}

const LOCATIONS_PINNED_KEY = 'dashboard.locationsPinned';

@Injectable({
    providedIn: 'root'
})
export class LayoutService {
    private readonly DARK_THEME_KEY = 'app-dark-theme'; // legacy key (bool only)
    private readonly THEME_MODE_KEY = 'app-theme-mode'; // new key storing light|dark|system
    private readonly PRESET_KEY = 'app-preset';
    private readonly PRIMARY_KEY = 'app-primary';
    private readonly SURFACE_KEY = 'app-surface';
    private readonly MENU_MODE_KEY = 'app-menu-mode';
    private readonly DENSITY_KEY = 'app-density';
    /** The cloud portal is a navigation-heavy console: keep the menu visible on desktop. Device mode keeps the overlay drawer. */
    private static readonly DEFAULT_MENU_MODE = typeof window !== 'undefined' && (window as any).__CLOUD_MODE__ ? 'static' : 'overlay';
    // Capture defaults so we can avoid persisting them unless the user actually changes something
    private readonly defaultConfig: layoutConfig = {
        preset: 'Aura',
        primary: 'lime',
        surface: 'gray',
        darkTheme: false,
        themeMode: 'system',
        menuMode: LayoutService.DEFAULT_MENU_MODE,
        density: 'comfortable'
    };
    _config: layoutConfig = {
        preset: 'Aura',
        primary: 'lime',
        surface: 'gray',
        darkTheme: false,
        themeMode: 'system',
        menuMode: LayoutService.DEFAULT_MENU_MODE,
        density: 'comfortable'
    };

    _state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false
    };

    layoutConfig = signal<layoutConfig>(this._config);

    layoutState = signal<LayoutState>(this._state);

    // Independent of the application menu; retained across location/root route changes.
    readonly locationSidebarCollapsed = signal(false);

    /**
     * Pinned: the dashboard's location navigator is a column beside the page (wide screens). Unpinned: it slides
     * out over the page and tucks away after a location is picked. Remembered per browser.
     */
    readonly locationSidebarPinned = signal(LayoutService.readPinned());

    setLocationSidebarPinned(pinned: boolean): void {
        this.locationSidebarPinned.set(pinned);
        try {
            localStorage.setItem(LOCATIONS_PINNED_KEY, String(pinned));
        } catch {
            /* storage unavailable: the choice lasts for this page only */
        }
    }

    private static readPinned(): boolean {
        try {
            return localStorage.getItem(LOCATIONS_PINNED_KEY) !== 'false';
        } catch {
            return true;
        }
    }

    private configUpdate = new Subject<layoutConfig>();

    private overlayOpen = new Subject<any>();

    private menuSource = new Subject<MenuChangeEvent>();

    private resetSource = new Subject();

    menuSource$ = this.menuSource.asObservable();

    resetSource$ = this.resetSource.asObservable();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    theme = computed(() => (this.isDarkTheme() ? 'dark' : 'light'));

    isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

    // System preference signal, updated via matchMedia listener.
    private systemPrefersDark = signal<boolean>(this.getSystemDarkTheme());

    isDarkTheme = computed(() => {
        const cfg = this.layoutConfig();
        if (cfg.themeMode === 'system' || !cfg.themeMode) {
            return this.systemPrefersDark();
        }
        return cfg.themeMode === 'dark';
    });

    getPrimary = computed(() => this.layoutConfig().primary);

    getSurface = computed(() => this.layoutConfig().surface);

    isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

    density = computed<Density>(() => this.layoutConfig().density ?? 'comfortable');

    /**
     * Whether the preset / primary / surface pickers are offered. The cloud
     * portal is locked to the brand preset (see cloud-app.config.ts) so this is
     * false there; device mode keeps the full configurator.
     */
    allowThemeCustomisation = signal<boolean>(typeof window === 'undefined' || !(window as any).__CLOUD_MODE__);

    transitionComplete = signal<boolean>(false);

    private initialized = false;

    constructor() {
        // On service init, set config from localStorage or system preference
        // Determine initial theme mode preference.
        const storedThemeMode = this.getStoredThemeMode();
        if (storedThemeMode) {
            this._config.themeMode = storedThemeMode as any;
        } else {
            // Fallback to legacy dark theme boolean if present
            const storedDark = this.getStoredDarkTheme();
            if (storedDark !== null) {
                this._config.themeMode = storedDark ? 'dark' : 'light';
            } else {
                this._config.themeMode = 'system';
            }
            // Keep darkTheme boolean in sync for backward compatibility
            this._config.darkTheme = this._config.themeMode === 'dark' ? true : this._config.themeMode === 'light' ? false : this.getSystemDarkTheme();
        }
        // Always initialize legacy darkTheme field to effective state
        this._config.darkTheme = this.isDarkTheme();
        const storedPreset = this.getStoredString(this.PRESET_KEY);
        if (storedPreset) this._config.preset = storedPreset;
        const storedPrimary = this.getStoredString(this.PRIMARY_KEY);
        if (storedPrimary) this._config.primary = storedPrimary;
        const storedSurface = this.getStoredString(this.SURFACE_KEY);
        if (storedSurface) this._config.surface = storedSurface;
        const storedMenuMode = this.getStoredString(this.MENU_MODE_KEY);
        if (storedMenuMode) this._config.menuMode = storedMenuMode;
        const storedDensity = this.getStoredString(this.DENSITY_KEY);
        if (storedDensity === 'comfortable' || storedDensity === 'compact') this._config.density = storedDensity;
        this.layoutConfig.set({ ...this._config });
        this.applyDensity(this._config.density);
        // Apply the resolved scheme immediately. The persistence effect below deliberately skips its
        // first run, so without this a stored "dark" (or system-dark) preference rendered light until
        // the user touched a theme control. index.html sets the class pre-bootstrap to avoid a flash;
        // this makes the service authoritative once it exists.
        this.toggleDarkMode();

        effect(() => {
            const config = this.layoutConfig();
            if (config) {
                this.onConfigUpdate();
            }
        });

        effect(() => {
            const config = this.layoutConfig();

            if (!this.initialized || !config) {
                this.initialized = true;
                return;
            }

            // Save config values to localStorage when changed
            // Persist only if value differs from default OR a prior stored value exists.
            this.persistThemeMode(config.themeMode || 'system');
            // Legacy dark theme flag: only persist if explicitly different from default system-driven state
            this.persistDarkThemeFlag();
            this.persistStringIfChanged(this.PRESET_KEY, config.preset, this.defaultConfig.preset);
            this.persistStringIfChanged(this.PRIMARY_KEY, config.primary, this.defaultConfig.primary);
            this.persistStringIfChanged(this.SURFACE_KEY, config.surface, this.defaultConfig.surface);
            this.persistStringIfChanged(this.MENU_MODE_KEY, config.menuMode, this.defaultConfig.menuMode);
            this.persistStringIfChanged(this.DENSITY_KEY, config.density, this.defaultConfig.density);
            this.applyDensity(config.density);
            this.handleDarkModeTransition(config);
        });

        // Listen for system preference changes when using 'system' mode
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const listener = (e: MediaQueryListEvent) => {
                this.systemPrefersDark.set(e.matches);
                const cfg = this.layoutConfig();
                if (cfg.themeMode === 'system') {
                    // Update legacy flag and trigger transition
                    this.layoutConfig.update((prev) => ({ ...prev, darkTheme: e.matches }));
                    // The config update above is a no-op when darkTheme already matched; apply directly.
                    this.toggleDarkMode();
                }
            };
            try {
                mq.addEventListener('change', listener);
            } catch {
                // Safari < 14
                mq.addListener(listener);
            }
        }
    }
    private getStoredString(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private setStoredString(key: string, value: string | undefined | null) {
        try {
            if (value === undefined || value === null) {
                localStorage.removeItem(key);
            } else {
                localStorage.setItem(key, value);
            }
        } catch {}
    }

    private getStoredDarkTheme(): boolean | null {
        try {
            const val = localStorage.getItem(this.DARK_THEME_KEY);
            if (val === null) return null;
            return val === 'true';
        } catch {
            return null;
        }
    }

    private setStoredDarkTheme(value: boolean) {
        try {
            localStorage.setItem(this.DARK_THEME_KEY, value ? 'true' : 'false');
        } catch {}
    }

    private getStoredThemeMode(): 'light' | 'dark' | 'system' | null {
        try {
            const val = localStorage.getItem(this.THEME_MODE_KEY);
            if (val === 'light' || val === 'dark' || val === 'system') return val;
            return null;
        } catch {
            return null;
        }
    }

    private setStoredThemeMode(mode: 'light' | 'dark' | 'system') {
        try {
            localStorage.setItem(this.THEME_MODE_KEY, mode);
        } catch {}
    }

    private removeStoredThemeMode() {
        try {
            localStorage.removeItem(this.THEME_MODE_KEY);
        } catch {}
    }

    // Only store theme mode if not default OR user previously had a stored preference.
    private persistThemeMode(mode: 'light' | 'dark' | 'system') {
        const alreadyStored = this.getStoredThemeMode() !== null;
        if (mode !== this.defaultConfig.themeMode || alreadyStored) {
            this.setStoredThemeMode(mode);
        } else {
            this.removeStoredThemeMode();
        }
    }

    // Legacy darkTheme: Only persist if there was already a stored dark flag OR the explicit mode is not system.
    private persistDarkThemeFlag() {
        const mode = this.layoutConfig().themeMode || 'system';
        const darkEffective = this.isDarkTheme();
        const legacyStored = this.getStoredDarkTheme();
        const shouldPersist = mode !== 'system' || legacyStored !== null;
        try {
            if (shouldPersist) {
                localStorage.setItem(this.DARK_THEME_KEY, darkEffective ? 'true' : 'false');
            } else {
                localStorage.removeItem(this.DARK_THEME_KEY);
            }
        } catch {}
    }

    private persistStringIfChanged(key: string, value: string | undefined | null, defaultVal: string | undefined | null) {
        const existing = this.getStoredString(key);
        const isDefault = value === defaultVal;
        try {
            if ((!isDefault || existing !== null) && value !== undefined && value !== null) {
                localStorage.setItem(key, value);
            } else if (isDefault && existing !== null) {
                // Remove if reverted to default
                localStorage.removeItem(key);
            }
            // If default and never stored, do nothing (avoid creating key)
        } catch {}
    }

    private getSystemDarkTheme(): boolean {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    }

    private handleDarkModeTransition(config: layoutConfig): void {
        if ((document as any).startViewTransition) {
            this.startViewTransition(config);
        } else {
            this.toggleDarkMode();
            this.onTransitionEnd();
        }
    }

    private startViewTransition(config: layoutConfig): void {
        const transition = (document as any).startViewTransition(() => {
            this.toggleDarkMode();
        });

        transition.ready
            .then(() => {
                this.onTransitionEnd();
            })
            .catch(() => {});
    }

    toggleDarkMode(): void {
        const dark = this.isDarkTheme();
        if (dark) {
            document.documentElement.classList.add('app-dark');
        } else {
            document.documentElement.classList.remove('app-dark');
        }
        // localStorage is updated in effect
    }

    setDensity(density: Density) {
        this.layoutConfig.update((prev) => ({ ...prev, density }));
    }

    private applyDensity(density: Density | undefined) {
        if (typeof document === 'undefined') return;
        const value = density ?? 'comfortable';
        if (value === 'comfortable') {
            delete document.documentElement.dataset['density'];
        } else {
            document.documentElement.dataset['density'] = value;
        }
    }

    private onTransitionEnd() {
        this.transitionComplete.set(true);
        setTimeout(() => {
            this.transitionComplete.set(false);
        });
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

            if (this.layoutState().overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            this.layoutState.update((prev) => ({ ...prev, staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive }));
        } else {
            this.layoutState.update((prev) => ({ ...prev, staticMenuMobileActive: !this.layoutState().staticMenuMobileActive }));

            if (this.layoutState().staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.layoutConfig() };
        this.configUpdate.next(this.layoutConfig());
    }

    onMenuStateChange(event: MenuChangeEvent) {
        this.menuSource.next(event);
    }

    reset() {
        this.resetSource.next(true);
    }
}
